import { useState } from 'react'
import {
  createCustomerPrivacyExportRequest,
  downloadCustomerPrivacyExport,
  fetchCustomerPrivacyExportRequestStatus,
  requestCustomerAccountDeletion,
  updateCustomerPrivacyConsents,
  CustomerPrivacyApiError,
  type CustomerPrivacyDashboard,
} from '../../customerPrivacyApi'
import { toPrivacyErrorMessage } from './privacyErrors'

type LoadDashboardFn = (mode: 'initial' | 'refresh') => void

type UsePrivacyOperationsInput = {
  privacyDashboard: CustomerPrivacyDashboard | null
  loadDashboard: LoadDashboardFn
  setPrivacyError: (value: string) => void
  setPrivacyNotice: (value: string) => void
}

export const usePrivacyOperations = ({
  privacyDashboard,
  loadDashboard,
  setPrivacyError,
  setPrivacyNotice,
}: UsePrivacyOperationsInput) => {
  const [exportRequesting, setExportRequesting] = useState(false)
  const [exportDownloading, setExportDownloading] = useState(false)
  const [consentsSaving, setConsentsSaving] = useState(false)
  const [deletionSubmitting, setDeletionSubmitting] = useState(false)

  const requestPrivacyExport = () => {
    setExportRequesting(true)
    setPrivacyError('')
    setPrivacyNotice('')
    void createCustomerPrivacyExportRequest()
      .then(() => {
        setPrivacyNotice('Richiesta export dati inviata. Preparazione in corso.')
        loadDashboard('refresh')
      })
      .catch((error) => {
        setPrivacyError(toPrivacyErrorMessage(error))
      })
      .finally(() => {
        setExportRequesting(false)
      })
  }

  const updatePrivacyConsents = (input: {
    marketingEmail: boolean
    promotionalNotifications: boolean
  }) => {
    setConsentsSaving(true)
    setPrivacyError('')
    setPrivacyNotice('')
    void updateCustomerPrivacyConsents(input)
      .then(() => {
        setPrivacyNotice('Preferenze privacy aggiornate correttamente.')
        loadDashboard('refresh')
      })
      .catch((error) => {
        setPrivacyError(toPrivacyErrorMessage(error))
      })
      .finally(() => {
        setConsentsSaving(false)
      })
  }

  const requestAccountDeletion = (password: string) => {
    setDeletionSubmitting(true)
    setPrivacyError('')
    setPrivacyNotice('')
    void requestCustomerAccountDeletion(password)
      .then(() => {
        setPrivacyNotice('Richiesta eliminazione account completata. Account anonimizzato.')
        loadDashboard('refresh')
      })
      .catch((error) => {
        setPrivacyError(toPrivacyErrorMessage(error))
      })
      .finally(() => {
        setDeletionSubmitting(false)
      })
  }

  const downloadPrivacyExport = () => {
    const requestId = privacyDashboard?.exportState.requestId
    if (!requestId) {
      setPrivacyError('Nessun export disponibile per il download')
      return
    }
    setExportDownloading(true)
    setPrivacyError('')
    setPrivacyNotice('')
    void fetchCustomerPrivacyExportRequestStatus(requestId)
      .then(async (statusPayload) => {
        if (!statusPayload.download?.url) {
          throw new CustomerPrivacyApiError(409, 'Export non pronto per il download')
        }
        const blob = await downloadCustomerPrivacyExport({
          downloadUrl: statusPayload.download.url,
        })
        const objectUrl = window.URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = objectUrl
        anchor.download = statusPayload.download.filename
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        window.URL.revokeObjectURL(objectUrl)
        setPrivacyNotice('Download export privacy avviato.')
        loadDashboard('refresh')
      })
      .catch((error) => {
        setPrivacyError(toPrivacyErrorMessage(error))
      })
      .finally(() => {
        setExportDownloading(false)
      })
  }

  return {
    exportRequesting,
    exportDownloading,
    consentsSaving,
    deletionSubmitting,
    requestPrivacyExport,
    downloadPrivacyExport,
    updatePrivacyConsents,
    requestAccountDeletion,
  }
}
